import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config';

const Register = ({ onLogin, setUser }) => {
  const navigate = useNavigate();

  // Debug: Log API_BASE_URL when component loads
  useEffect(() => {
    console.log('Register component loaded - API_BASE_URL:', API_BASE_URL);
    console.log('Current hostname:', window.location.hostname);
    console.log('Current URL:', window.location.href);
  }, []);

  // Redirect to dashboard if already logged in
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      navigate('/dashboard');
    }
  }, [navigate]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    userType: 'talent',
    phone: '',
    dateOfBirth: '',
    heightFeet: '',
    heightInches: '',
    gender: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      // Use .php extension to avoid .htaccess rewrite issues with POST requests
      const registerUrl = `${API_BASE_URL}/auth/register.php`;
      console.log('Sending registration request to:', registerUrl);
      console.log('Request method: POST');
      console.log('Request data:', formData);
      
      const response = await axios.post(registerUrl, formData, {
        headers: {
          'Content-Type': 'application/json',
        },
        withCredentials: false
      });
      console.log('Registration response:', response);
      console.log('Registration response data:', response.data);
      
      // Handle different response formats
      const token = response.data?.token || response.data?.data?.token;
      const talent = response.data?.talent || response.data?.user || response.data?.data?.user || response.data?.data?.talent;
      
      if (!token) {
        console.error('No token in response. Full response:', response);
        console.error('Response data:', response.data);
        console.error('Response status:', response.status);
        setError('Registration successful but no authentication token received. Please try logging in. Check console for details.');
        setLoading(false);
        return;
      }
      
      // Create user object if not provided
      const userData = talent || {
        id: response.data?.id || response.data?.userId,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        userType: formData.userType,
        phone: formData.phone,
        dateOfBirth: formData.dateOfBirth,
        heightFeet: formData.heightFeet,
        heightInches: formData.heightInches,
        gender: formData.gender,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        country: formData.country
      };
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      setUser(userData);
      onLogin(true);
      navigate('/dashboard');
    } catch (err) {
      console.error('Registration error:', err);
      console.error('Error response:', err.response);
      console.error('Error response data:', err.response?.data);
      console.error('Error response status:', err.response?.status);
      console.error('API URL being used:', `${API_BASE_URL}/auth/register`);
      console.error('Error message:', err.message);
      console.error('Error code:', err.code);
      
      if (err.response?.status === 400) {
        const errorMsg = err.response?.data?.message || err.response?.data?.error || 'Invalid registration data. Please check your information.';
        setError(errorMsg);
      } else if (err.response?.status === 409) {
        setError('An account with this email already exists. Please log in instead.');
      } else if (err.response?.status >= 500) {
        // Show detailed error message from server
        const serverError = err.response?.data?.error || err.response?.data?.message || 'Unknown server error';
        const errorMsg = `Server error: ${serverError}. Check console for details.`;
        console.error('Server error details:', err.response?.data);
        setError(errorMsg);
      } else if (err.message === 'Network Error' || err.code === 'ECONNREFUSED' || err.code === 'ERR_NETWORK') {
        const apiUrl = `${API_BASE_URL}/auth/register`;
        setError(`Unable to connect to the server at ${apiUrl}. Please check your internet connection and try again. If the problem persists, contact support.`);
      } else {
        const errorMsg = err.response?.data?.message || err.response?.data?.error || 'Registration failed. Please try again.';
        setError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-container">
      <h2>Sign Up</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="userType">I am a:</label>
          <select
            id="userType"
            name="userType"
            value={formData.userType}
            onChange={handleChange}
            required
          >
            <option value="talent">Talent</option>
            <option value="writer">Talent's representative</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="firstName">First Name:</label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="lastName">Last Name:</label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="email">Email:</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            autoComplete="email"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="phone">Phone:</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="dateOfBirth">Date of Birth:</label>
          <input
            type="date"
            id="dateOfBirth"
            name="dateOfBirth"
            value={formData.dateOfBirth || ''}
            onChange={handleChange}
            onBlur={(e) => {
              // Ensure date is in correct format on blur
              const dateValue = e.target.value.trim();
              if (dateValue && !/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
                console.warn('Invalid date format on blur:', dateValue);
              }
            }}
            min="1900-01-01"
            max={new Date().toISOString().split('T')[0]}
            style={{
              fontSize: '16px',
              padding: '10px',
              width: '100%',
              fontFamily: 'inherit',
              letterSpacing: '0.5px',
              textAlign: 'left'
            }}
            data-date-format="YYYY-MM-DD"
            placeholder="YYYY-MM-DD"
            autoComplete="bday"
          />
        </div>

        <div className="form-group">
          <label htmlFor="height">Height:</label>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <select
              id="heightFeet"
              name="heightFeet"
              value={formData.heightFeet}
              onChange={handleChange}
              style={{
                flex: '1',
                padding: '12px 16px',
                border: '2px solid #e9ecef',
                borderRadius: '8px',
                fontSize: '16px',
                fontFamily: 'inherit'
              }}
            >
              <option value="">Feet</option>
              {Array.from({ length: 8 }, (_, i) => i + 1).map(feet => (
                <option key={feet} value={feet}>{feet} ft</option>
              ))}
            </select>
            <select
              id="heightInches"
              name="heightInches"
              value={formData.heightInches}
              onChange={handleChange}
              style={{
                flex: '1',
                padding: '12px 16px',
                border: '2px solid #e9ecef',
                borderRadius: '8px',
                fontSize: '16px',
                fontFamily: 'inherit'
              }}
            >
              <option value="">Inches</option>
              {Array.from({ length: 12 }, (_, i) => i).map(inches => (
                <option key={inches} value={inches}>{inches} in</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="gender">Gender:</label>
          <select
            id="gender"
            name="gender"
            value={formData.gender}
            onChange={handleChange}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '2px solid #e9ecef',
              borderRadius: '8px',
              fontSize: '16px',
              fontFamily: 'inherit',
              backgroundColor: '#fff'
            }}
          >
            <option value="">Select Gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="address">Address:</label>
          <input
            type="text"
            id="address"
            name="address"
            value={formData.address}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="city">City:</label>
          <input
            type="text"
            id="city"
            name="city"
            value={formData.city}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="state">State:</label>
          <input
            type="text"
            id="state"
            name="state"
            value={formData.state}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="zipCode">ZIP Code:</label>
          <input
            type="text"
            id="zipCode"
            name="zipCode"
            value={formData.zipCode}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="country">Country:</label>
          <input
            type="text"
            id="country"
            name="country"
            value={formData.country}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="password">Password:</label>
          <div className="password-input-wrapper">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              autoComplete="new-password"
              required
              className="password-input"
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              tabIndex={0}
            >
              {showPassword ? (
                <span className="password-icon">👁️</span>
              ) : (
                <span className="password-icon">👁️‍🗨️</span>
              )}
            </button>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="confirmPassword">Confirm Password:</label>
          <div className="password-input-wrapper">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              autoComplete="new-password"
              required
              className="password-input"
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              tabIndex={0}
            >
              {showConfirmPassword ? (
                <span className="password-icon">👁️</span>
              ) : (
                <span className="password-icon">👁️‍🗨️</span>
              )}
            </button>
          </div>
        </div>

        {error && <div className="error">{error}</div>}
        
        <button type="submit" className="btn" disabled={loading}>
          {loading ? 'Creating Account...' : 'Join'}
        </button>
      </form>
      
      <p style={{ textAlign: 'center', marginTop: '20px' }}>
        Already have an account? <Link to="/login">Login here</Link>
      </p>
      <p style={{ textAlign: 'center', marginTop: '10px' }}>
        <Link to="/">← Back to Home</Link>
      </p>
    </div>
  );
};

export default Register;
